import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import { toast } from "sonner";
import {
  Loader2, Plus, X, Copy, Archive, CalendarDays, Users, Play, Pause,
  Info, Lock, CheckCircle2, Clock, RefreshCw,
} from "lucide-react";
import { PlanCalendar, BatchCalendar } from "../../components/ProgrammeCalendars";

/**
 * Programmes: a worksheet plan written in day numbers, not dates.
 *
 * The whole point of this screen is that it is filled in once. "Day 1:
 * Fractions, day 2: Indices" is true of every batch that ever takes the
 * course; the dates are not, so they live on the enrolment instead. Next
 * September is a new enrolment of the same programme, which is one date
 * rather than a term's retyping.
 *
 * Both screens show a calendar, and they are not the same calendar. The
 * editor's squares are Day 1, Day 2, Day 3 in the shape of a month - a month
 * to read, with no dates in it. The enrolment's squares are real dates for
 * one batch, and are read-only. Keeping those apart is what makes the reuse
 * work; putting a date into the editor is the obvious-looking change that
 * would quietly undo it.
 */

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const pretty = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T12:00:00");
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
};


/** Build or edit the plan. Day numbers only — no dates anywhere in here. */
function PlanEditor({ plan, sheets, onClose, onSaved }) {
  const isNew = !plan;
  const [title, setTitle] = useState(plan?.title || "");
  const [notes, setNotes] = useState(plan?.notes || "");
  const [weekdays, setWeekdays] = useState(plan?.weekdays || [0, 1, 2, 3, 4]);
  const [grace, setGrace] = useState(String(plan?.grace_days ?? 3));
  const [lengthDays, setLengthDays] = useState(String(plan?.length_days ?? ""));
  const [items, setItems] = useState(
    (plan?.items || []).map((i) => ({ set_id: i.set_id, day: i.day, kind: i.kind || null }))
  );
  // Days deliberately left blank. Separate from `items` rather than a kind
  // of item, because the thing being recorded is a decision not to set work
  // - there is no worksheet to point at.
  const [skipped, setSkipped] = useState(
    (plan?.skipped_days || []).map(Number)
  );
  const [saving, setSaving] = useState(false);

  const toggleDay = (n) =>
    setWeekdays((w) => (w.includes(n) ? w.filter((x) => x !== n) : [...w, n].sort()));

  // A day gives out one worksheet: `assignments` are keyed on their programme
  // day, so a second worksheet sharing a day would never reach a child.
  // Filling an occupied square replaces what is in it.
  const assignDay = (day, setId) =>
    setItems((x) => [...x.filter((i) => Number(i.day) !== day),
                     { set_id: setId, day, kind: null }]);

  const clearDay = (day) => setItems((x) => x.filter((i) => Number(i.day) !== day));

  /**
   * Cross a day off, or put it back.
   *
   * Crossing also clears whatever was on it: "no worksheet this day" and a
   * worksheet sitting on that day are a contradiction, and leaving both
   * would mean the grid and the children disagreed about what happens.
   */
  const skipDay = (day, on) => {
    setSkipped((x) => (on ? [...new Set([...x, day])].sort((a, b) => a - b)
                          : x.filter((d) => d !== day)));
    if (on) clearDay(day);
  };

  // Dragging onto an occupied square swaps the two, which is what a tutor
  // reordering a week means by it - nothing is lost off the end.
  const moveDay = (from, to) =>
    setItems((x) => x.map((i) => {
      const d = Number(i.day);
      if (d === from) return { ...i, day: to };
      if (d === to) return { ...i, day: from };
      return i;
    }));

  const save = () => {
    if (!title.trim()) return toast.error("Give the programme a name");
    if (items.length === 0) return toast.error("Add at least one worksheet");
    if (weekdays.length === 0) return toast.error("Pick at least one working day");
    setSaving(true);
    const body = {
      title, notes, weekdays,
      grace_days: Math.max(0, Number(grace) || 0),
      length_days: Math.max(0, Number(lengthDays) || 0),
      skipped_days: skipped,
      items: items.map((i) => ({ set_id: i.set_id, day: i.day, kind: i.kind })),
    };
    const req = isNew
      ? api.post("/admin/programmes", body)
      : api.patch(`/admin/programmes/${plan.id}`, body);
    req
      .then((r) => {
        if (!isNew && r.data?.already_opened > 0) {
          toast.success(`Saved. ${r.data.already_opened} day(s) already open are unchanged.`);
        } else {
          toast.success(isNew ? "Programme created" : "Saved");
        }
        onSaved(); onClose();
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not save"))
      .finally(() => setSaving(false));
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl my-6 shadow-xl">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
          <h2 className="font-semibold text-slate-800">
            {isNew ? "New programme" : `Edit — ${plan.title}`}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-5">
          <div className="flex items-start gap-2.5 rounded-lg border border-blue-200 bg-blue-50 px-3.5 py-2.5">
            <Info size={15} className="mt-0.5 shrink-0 text-blue-600" />
            <p className="text-sm text-blue-900">
              A programme is a plan in <strong>day numbers</strong>, not dates — four weeks of
              squares is roughly a month. You set the real dates once per batch when you give it
              out, so the same plan runs in 2026, 2027 and 2028 without you touching the days.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              <span className="block text-slate-600 mb-1">Name</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="Year 9 — Autumn daily practice"
                className="w-full border border-slate-300 rounded-lg px-3 py-2" />
            </label>
            <label className="text-sm">
              <span className="block text-slate-600 mb-1">Catch-up window</span>
              <div className="flex items-center gap-2">
                <input type="number" min="0" value={grace}
                  onChange={(e) => setGrace(e.target.value)}
                  className="w-20 border border-slate-300 rounded-lg px-3 py-2" />
                <span className="text-sm text-slate-500">more school days</span>
              </div>
              <span className="block text-xs text-slate-500 mt-1">
                {Number(grace) > 0
                  ? `Opens on its day and stays open for ${grace} more school day${Number(grace) === 1 ? "" : "s"}, then closes.`
                  : "Closes at the end of its own day."}
              </span>
            </label>
          </div>

          <div>
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1.5">
              <p className="text-sm text-slate-600">Which days count</p>
              <span className="flex gap-1.5">
                <button type="button" onClick={() => setWeekdays([0, 1, 2, 3, 4, 5, 6])}
                  className="text-xs border border-slate-300 rounded-lg px-2.5 py-1 hover:bg-slate-50">
                  Every day
                </button>
                <button type="button" onClick={() => setWeekdays([0, 1, 2, 3, 4])}
                  className="text-xs border border-slate-300 rounded-lg px-2.5 py-1 hover:bg-slate-50">
                  School days
                </button>
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {DAY_NAMES.map((d, n) => (
                <button key={d} type="button" onClick={() => toggleDay(n)}
                  className={`text-sm rounded-lg border px-3 py-1.5 ${
                    weekdays.includes(n)
                      ? "bg-slate-800 text-white border-slate-800"
                      : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
                  {d}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {weekdays.length === 7
                ? "Every day counts, so day 1 is the 1st of the month whatever weekday that is, day 2 the 2nd, and so on."
                : "Day numbers skip everything not selected, and skip the holidays you set per batch."}
            </p>
          </div>

          <label className="text-sm block">
            <span className="block text-slate-600 mb-1">How many days long</span>
            <div className="flex flex-wrap items-center gap-2">
              <input type="number" min="0" value={lengthDays}
                onChange={(e) => setLengthDays(e.target.value)}
                placeholder="30"
                className="w-24 border border-slate-300 rounded-lg px-3 py-2" />
              {[28, 30, 31].map((n) => (
                <button key={n} type="button" onClick={() => setLengthDays(String(n))}
                  className="text-xs border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-slate-50">
                  {n}
                </button>
              ))}
            </div>
            <span className="block text-xs text-slate-500 mt-1">
              Set this and the calendar draws exactly that many squares and tells you if any of
              them is still empty. Leave it blank and the plan is simply as long as its last
              worksheet.
            </span>
          </label>

          <label className="text-sm block">
            <span className="block text-slate-600 mb-1">Notes to yourself (optional)</span>
            <input value={notes || ""} onChange={(e) => setNotes(e.target.value)}
              placeholder="Follows the autumn scheme of work"
              className="w-full border border-slate-300 rounded-lg px-3 py-2" />
          </label>

          <PlanCalendar items={items} weekdays={weekdays} skipped={skipped} onSkip={skipDay}
            length={Math.max(0, Number(lengthDays) || 0)} sheets={sheets}
            onAssign={assignDay} onClear={clearDay} onMove={moveDay} />
        </div>

        <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
          <button onClick={save} disabled={saving}
            className="px-4 py-2 text-sm font-semibold bg-slate-800 text-white rounded-lg disabled:opacity-50">
            {saving ? "Saving…" : isNew ? "Create programme" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}


/** Give a programme to a batch: the one place real dates are entered. */
function EnrolModal({ plan, groups, students, onClose, onDone }) {
  const [groupId, setGroupId] = useState(groups[0]?.id || "");
  const [extra, setExtra] = useState([]);
  const [start, setStart] = useState(new Date().toISOString().slice(0, 10));
  const [year, setYear] = useState("");
  const [holidayText, setHolidayText] = useState("");
  const [marking, setMarking] = useState("self");
  const [saving, setSaving] = useState(false);

  const holidays = holidayText.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);

  const go = () => {
    if (!groupId && extra.length === 0) return toast.error("Pick a group or some students");
    if (!start) return toast.error("Pick a start date");
    setSaving(true);
    api.post("/admin/enrolments", {
      programme_id: plan.id,
      group_id: groupId || null,
      student_ids: extra,
      start_date: start,
      holidays,
      academic_year: year || null,
      marking,
    })
      .then((r) => {
        const o = r.data?.settled?.opened || 0;
        toast.success(o > 0
          ? `Started. ${o} day${o === 1 ? "" : "s"} already open.`
          : "Started. The first worksheet unlocks on its day.");
        onDone(); onClose();
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not start it"))
      .finally(() => setSaving(false));
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-xl w-full max-w-lg my-6 shadow-xl">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
          <h2 className="font-semibold text-slate-800">Start — {plan.title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-500">
            {plan.day_count} worksheet{plan.day_count === 1 ? "" : "s"}. Each one unlocks on its own
            day and closes after the catch-up window. Nothing is created until the day arrives, so
            nobody can read ahead.
          </p>

          <label className="text-sm block">
            <span className="block text-slate-600 mb-1">Group</span>
            <select value={groupId} onChange={(e) => setGroupId(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2">
              <option value="">— none —</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name} ({g.student_count})</option>
              ))}
            </select>
          </label>

          <div>
            <p className="text-sm text-slate-600 mb-1.5">Plus anyone else (optional)</p>
            <div className="flex flex-wrap gap-1.5">
              {students.map((s) => (
                <button key={s.id} type="button"
                  onClick={() => setExtra((x) => (x.includes(s.id) ? x.filter((y) => y !== s.id) : [...x, s.id]))}
                  className={`text-sm rounded-full border px-3 py-1.5 ${
                    extra.includes(s.id) ? "bg-slate-800 text-white border-slate-800" : "border-slate-300 hover:bg-slate-50"}`}>
                  {s.name}
                </button>
              ))}
              {students.length === 0 && <p className="text-sm text-slate-400">No student accounts yet.</p>}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              <span className="block text-slate-600 mb-1">Day 1 falls on</span>
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2" />
              <span className="block text-xs text-slate-500 mt-1">
                If this is not a working day, day 1 moves to the next one.
              </span>
            </label>
            <label className="text-sm">
              <span className="block text-slate-600 mb-1">Academic year (optional)</span>
              <input value={year} onChange={(e) => setYear(e.target.value)} placeholder="2026-27"
                className="w-full border border-slate-300 rounded-lg px-3 py-2" />
            </label>
          </div>

          <label className="text-sm block">
            <span className="block text-slate-600 mb-1">Holidays to skip (optional)</span>
            <textarea value={holidayText} onChange={(e) => setHolidayText(e.target.value)}
              rows={2} placeholder="2026-12-21 2026-12-22 2026-12-23"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 font-mono text-xs" />
            <span className="block text-xs text-slate-500 mt-1">
              Dates as YYYY-MM-DD, separated by spaces or commas. {holidays.length > 0
                ? `${holidays.length} date${holidays.length === 1 ? "" : "s"} will be skipped.`
                : "The programme pauses on these days and picks up after."}
            </span>
          </label>

          <label className="text-sm block">
            <span className="block text-slate-600 mb-1">Marking</span>
            <select value={marking} onChange={(e) => setMarking(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2">
              <option value="self">They mark themselves</option>
              <option value="tutor">I mark it</option>
            </select>
          </label>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
          <button onClick={go} disabled={saving}
            className="px-4 py-2 text-sm font-semibold bg-slate-800 text-white rounded-lg disabled:opacity-50">
            {saving ? "Starting…" : "Start it"}
          </button>
        </div>
      </div>
    </div>
  );
}


/** One batch's real calendar: what opened, what is open, what is still to come. */
function ScheduleModal({ enrolmentId, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [holidayText, setHolidayText] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/enrolments/${enrolmentId}`)
      .then((r) => { setD(r.data); setHolidayText((r.data.enrolment.holidays || []).join(" ")); })
      .catch((e) => setErr(e.response?.data?.detail || "Could not open it"));
  }, [enrolmentId]);

  useEffect(() => { load(); }, [load]);

  const saveHolidays = () => {
    const holidays = holidayText.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    api.patch(`/admin/enrolments/${enrolmentId}`, { holidays })
      .then(() => { toast.success("Dates moved"); load(); onChanged && onChanged(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not save"));
  };

  const pause = (paused) =>
    api.patch(`/admin/enrolments/${enrolmentId}`, { paused })
      .then(() => { toast.success(paused ? "Paused" : "Running again"); load(); onChanged && onChanged(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not change it"));

  const e = d?.enrolment;
  const rows = d?.schedule || [];

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl my-6 shadow-xl">
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-slate-200">
          <div className="min-w-0">
            <h2 className="font-semibold text-slate-800 truncate">
              {d?.programme?.title || "Programme"} — {e?.group_name || "selected students"}
            </h2>
            {e && (
              <p className="text-xs text-slate-500 mt-0.5">
                day 1 on {pretty(e.start_date)} · {(e.student_ids || []).length} student
                {(e.student_ids || []).length === 1 ? "" : "s"}
                {d.programme?.grace_days != null && ` · ${d.programme.grace_days} school day catch-up`}
                {e.last_settled_at && ` · checked ${new Date(e.last_settled_at).toLocaleString("en-GB")}`}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {e && (
              <button onClick={() => pause(!e.paused)}
                className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-slate-50">
                {e.paused ? <><Play size={12} /> Resume</> : <><Pause size={12} /> Pause</>}
              </button>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
          </div>
        </div>

        <div className="p-5">
          {err && <p className="text-sm text-red-700">{err}</p>}
          {!d && !err && (
            <p className="flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 size={15} className="animate-spin" /> Loading…
            </p>
          )}

          {e?.paused && (
            <p className="mb-4 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3.5 py-2.5">
              Paused. Nothing new will unlock and nothing will close until you resume it.
            </p>
          )}

          {d && (
            <>
              <BatchCalendar calendar={d.calendar} schedule={rows} today={d.today} />

              <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mt-5 mb-2">
                Day by day
              </p>
              <div className="space-y-1.5">
                {rows.map((r) => (
                  <div key={r.day}
                    className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-3 py-2 ${
                      r.state === "open" ? "border-green-300 bg-green-50"
                        : r.state === "closed" ? "border-slate-200 bg-slate-50"
                          : "border-dashed border-slate-300"}`}>
                    <span className="text-xs font-bold text-slate-400 w-12 shrink-0">Day {r.day}</span>
                    <span className="font-medium text-sm text-slate-800 flex-1 min-w-[130px]">
                      {r.title}
                      {r.question_count === 0 && (
                        <span className="text-xs text-red-600 font-normal"> — no questions in it</span>
                      )}
                    </span>
                    <span className="text-xs text-slate-500">
                      {pretty(r.opens_on)}
                      {r.closes_after !== r.opens_on ? ` → ${pretty(r.closes_after)}` : ""}
                    </span>
                    {r.state === "open" && (
                      <span className="text-xs font-semibold text-green-700 inline-flex items-center gap-1">
                        <Clock size={11} />
                        {r.days_left > 0 ? `${r.days_left} day${r.days_left === 1 ? "" : "s"} left` : "last day"}
                        {r.handed_in > 0 ? ` · ${r.handed_in} in` : ""}
                      </span>
                    )}
                    {r.state === "closed" && (
                      <span className="text-xs font-semibold text-slate-500 inline-flex items-center gap-1">
                        <Lock size={11} /> closed{r.handed_in > 0 ? ` · ${r.handed_in} in` : ""}
                      </span>
                    )}
                    {r.state === "waiting" && (
                      <span className="text-xs text-slate-400">not yet</span>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-5 border-t border-slate-200 pt-4">
                <p className="text-sm font-semibold text-slate-700 mb-1">Holidays</p>
                <p className="text-xs text-slate-500 mb-2">
                  Adding a date pushes every day that has not opened yet further back. Days already
                  open keep their dates.
                </p>
                <textarea value={holidayText ?? ""} onChange={(ev) => setHolidayText(ev.target.value)}
                  rows={2}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 font-mono text-xs" />
                <button onClick={saveHolidays}
                  className="mt-2 text-xs font-semibold border border-slate-300 rounded-lg px-3 py-1.5 hover:bg-slate-50">
                  Save holidays
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


export default function AdminProgrammes() {
  const [plans, setPlans] = useState([]);
  const [enrolments, setEnrolments] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [groups, setGroups] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(undefined);   // undefined closed, null new
  const [enrolFor, setEnrolFor] = useState(null);
  const [showing, setShowing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/admin/programmes"),
      api.get("/admin/enrolments"),
    ])
      .then(([p, e]) => {
        setPlans(p.data || []);
        setEnrolments(e.data?.enrolments || []);
        const moved = e.data?.settled;
        if (moved && (moved.opened || moved.closed)) {
          toast.success(
            `${moved.opened} worksheet(s) opened, ${moved.closed} closed.`);
        }
      })
      .catch((err) => toast.error(err.response?.data?.detail || "Could not load programmes"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    api.get("/admin/sets").then((r) => setSheets(r.data || [])).catch(() => setSheets([]));
    api.get("/admin/groups").then((r) => setGroups(r.data || [])).catch(() => setGroups([]));
    api.get("/admin/students?role=student")
      .then((r) => setStudents((r.data.users || []).filter((u) => u.role === "student")))
      .catch(() => setStudents([]));
  }, [load]);

  const duplicate = (id) =>
    api.post(`/admin/programmes/${id}/duplicate`)
      .then(() => { toast.success("Copied — edit the copy for next year"); load(); })
      .catch(() => toast.error("Could not copy it"));

  const archive = (p) =>
    api.patch(`/admin/programmes/${p.id}`, { archived: true })
      .then(() => { toast.success("Archived — nothing is deleted"); load(); })
      .catch(() => toast.error("Could not archive it"));

  return (
    <AdminShell title="Programmes"
      subtitle="A plan in day numbers, built once and started again each year">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <p className="text-sm text-slate-500 max-w-xl">
          A plan in day numbers — day 1, day 2, day 3 — that drip-feeds worksheets to a batch.
          Build it once and start it again for next year's group with a new date.
        </p>
        <button onClick={() => setEditing(null)}
          className="inline-flex items-center gap-1.5 bg-slate-800 text-white text-sm font-semibold rounded-lg px-4 py-2">
          <Plus size={15} /> New programme
        </button>
      </div>

      {loading && (
        <p className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 size={15} className="animate-spin" /> Loading…
        </p>
      )}

      {!loading && plans.length === 0 && (
        <div className="rounded-xl border border-slate-200 bg-white py-14 text-center">
          <CalendarDays size={26} className="mx-auto text-slate-300 mb-3" />
          <p className="font-semibold text-slate-600">No programmes yet</p>
          <p className="text-sm text-slate-400 mt-1">
            Build your worksheets first, then put them in day order here.
          </p>
        </div>
      )}

      {plans.length > 0 && (
        <>
          <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-2">The plans</p>
          <div className="space-y-2">
            {plans.map((p) => (
              <div key={p.id}
                className="rounded-xl border border-slate-200 bg-white px-4 py-3.5 flex flex-wrap items-center gap-x-4 gap-y-2">
                <span className="flex-1 min-w-[170px]">
                  <span className="block font-medium text-slate-900">{p.title}</span>
                  <span className="block text-xs text-slate-500">
                    {p.day_count} worksheet{p.day_count === 1 ? "" : "s"}
                    {p.plan_length ? ` of ${p.plan_length} days` : ""} ·{" "}
                    {(p.weekdays || []).length === 7
                      ? "every day"
                      : (p.weekdays || []).map((n) => DAY_NAMES[n]).join(" ")} ·{" "}
                    {p.grace_days} day catch-up
                    {p.times_used > 0
                      ? ` · used by ${p.times_used} batch${p.times_used === 1 ? "" : "es"}`
                      : " · never started"}
                  </span>
                  {(p.empty_days || []).length > 0 && (
                    <span className="block text-xs text-amber-700 font-semibold mt-0.5">
                      {p.empty_days.length} day{p.empty_days.length === 1 ? "" : "s"} still empty
                    </span>
                  )}
                  {p.notes && <span className="block text-xs text-slate-400 mt-0.5">{p.notes}</span>}
                </span>
                <span className="flex flex-wrap items-center gap-1.5">
                  <button onClick={() => setEnrolFor(p)}
                    className="text-xs font-semibold inline-flex items-center gap-1 bg-slate-800 text-white rounded-lg px-2.5 py-1.5">
                    <Users size={12} /> Start for a batch
                  </button>
                  <button onClick={() => setEditing(p)}
                    className="text-xs border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-slate-50">
                    Edit
                  </button>
                  <button onClick={() => duplicate(p.id)} title="Copy it for another year"
                    className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 hover:bg-slate-50">
                    <Copy size={12} />
                  </button>
                  <button onClick={() => archive(p)} title="Archive"
                    className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 hover:bg-slate-50">
                    <Archive size={12} />
                  </button>
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {enrolments.length > 0 && (
        <div className="mt-7">
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-400">
              Running now
            </p>
            <button onClick={load}
              className="text-xs inline-flex items-center gap-1 text-slate-500 hover:text-slate-800">
              <RefreshCw size={11} /> Check for today
            </button>
          </div>
          <div className="space-y-2">
            {enrolments.map((e) => (
              <button key={e.id} onClick={() => setShowing(e.id)}
                className="w-full text-left rounded-xl border border-slate-200 bg-white px-4 py-3.5 hover:bg-slate-50/70 flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="flex-1 min-w-[170px]">
                  <span className="block font-medium text-slate-900">
                    {e.group_name || "Selected students"} — {e.programme_title}
                  </span>
                  <span className="block text-xs text-slate-500">
                    day 1 on {pretty(e.start_date)} · {e.students} student{e.students === 1 ? "" : "s"} ·{" "}
                    {e.days_opened} of {e.days_total} opened
                    {e.academic_year ? ` · ${e.academic_year}` : ""}
                  </span>
                </span>
                {e.paused
                  ? <span className="text-xs text-amber-700 inline-flex items-center gap-1 font-semibold"><Pause size={12} /> paused</span>
                  : e.next_opens_on
                    ? <span className="text-xs text-slate-500">next: {pretty(e.next_opens_on)}</span>
                    : <span className="text-xs text-green-700 inline-flex items-center gap-1 font-semibold"><CheckCircle2 size={12} /> all out</span>}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-2">
            Worksheets open and close when somebody opens this screen or a child opens theirs —
            there is no overnight job, so nothing can fail quietly.
          </p>
        </div>
      )}

      {editing !== undefined && (
        <PlanEditor plan={editing} sheets={sheets}
          onClose={() => setEditing(undefined)} onSaved={load} />
      )}
      {enrolFor && (
        <EnrolModal plan={enrolFor} groups={groups} students={students}
          onClose={() => setEnrolFor(null)} onDone={load} />
      )}
      {showing && (
        <ScheduleModal enrolmentId={showing}
          onClose={() => setShowing(null)} onChanged={load} />
      )}
    </AdminShell>
  );
}
